import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from '@thd-olt-component-react/router';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  Pagination,
  PaginationCompact,
  PaginationItem
} from '@one-thd/sui-atomic-components';
import {
  arrayOf,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import {
  MAX_INDEXABLE_PAGES,
  DEDICATED_REVIEW_PAGE_SIZE,
  MAX_REVIEW_API_RESULTS
} from '../constants';

const getReviewCanonicalUrl = (data, category) => {
  if (category === 'product') {
    const canonicalUrl = data?.product?.identifiers?.canonicalUrl;
    return canonicalUrl?.replace('/p/', '/p/reviews/');
  }
  const canonicalUrl = data?.storeSearch?.stores?.[0]?.storeDetailsPageLink;
  return canonicalUrl?.concat('/reviews');

};

const RatingReviewPageLinks = ({
  itemId = '',
  storeId = '',
  category = 'product',
  channel = 'desktop',
  totalReviews = 0,
  paginationProps = {}
}) => {
  const navigate = useNavigate();

  const {
    page,
    count = 1,
    onChange = () => { }
  } = paginationProps;
  const { channel: contextChannel, path } = useContext(ExperienceContext);
  const { page: reviewPageNumber } = useParams();
  const isMobile = channel === 'mobile' || contextChannel === 'mobile';

  const productData = useDataModel('product', {
    skip: !itemId || category !== 'product',
    variables: { itemId }
  });

  const storeData = useDataModel('storeSearch', {
    skip: !storeId || category !== 'store',
    variables: {
      storeSearchInput: storeId,
      pagesize: '1'
    }
  });

  const { data, error, loading } = category === 'product' ? productData : storeData;

  if (!data || error || loading) {
    return null;
  }

  const reviewCanonicalUrl = getReviewCanonicalUrl(data, category);
  const isReviewPage = path?.includes('/reviews');
  const reviewPageCount = Math.max(
    1,
    Math.ceil((Number(totalReviews) || 0) / DEDICATED_REVIEW_PAGE_SIZE)
  );
  const lastPageToIndex = Math.min(reviewPageCount, MAX_INDEXABLE_PAGES);

  // The reviews API is limited to a maximum of results
  // which corresponds to max pgs at 30 reviews per pg and 50 pgs at 10 reviews per pg
  const maxReviewPageCount = Math.ceil(MAX_REVIEW_API_RESULTS / DEDICATED_REVIEW_PAGE_SIZE);
  const maxPageNumber = Math.min(count, (isReviewPage ? maxReviewPageCount : 50));

  // Determine active page number based on either the review page param or the passed in active page prop
  const activePageNumber = (isReviewPage && !!reviewPageNumber)
    ? Number.parseInt(reviewPageNumber, 10)
    : page;

  const handleChange = (event, value) => {
    event.preventDefault();
    if (isReviewPage && reviewCanonicalUrl) {
      navigate(`${reviewCanonicalUrl}/${value}`);
    }

    onChange(event, value);
  };

  const getPaginationItem = (item) => {
    // Business rule
    // don't link past the last page with content or MAX_INDEXABLE_PAGES so as to avoid wasting crawl budget
    const includeHref = item?.page && item.page <= lastPageToIndex && !!reviewCanonicalUrl;
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <PaginationItem {...item} href={includeHref ? `${reviewCanonicalUrl}/${item.page}` : undefined} />;
  };

  const componentProps = {
    ...paginationProps,
    count: maxPageNumber,
    onChange: handleChange,
    renderItem: getPaginationItem,
    page: activePageNumber
  };

  return (
    <div className="sui-w-full" data-component="RatingReviewPageLinks">
      {isMobile
        // eslint-disable-next-line react/jsx-props-no-spreading
        ? <PaginationCompact {...componentProps} showFirstButton showLastButton />
        // eslint-disable-next-line react/jsx-props-no-spreading
        : <Pagination {...componentProps} />}
    </div>
  );
};

RatingReviewPageLinks.displayName = 'RatingReviewPageLinks';

RatingReviewPageLinks.propTypes = {
  itemId: PropTypes.string,
  storeId: PropTypes.string,
  category: PropTypes.string,
  channel: PropTypes.string,
  totalReviews: PropTypes.number,
  paginationProps: PropTypes.shape({
    page: PropTypes.number,
    count: PropTypes.number,
    onChange: PropTypes.func
  })
};

RatingReviewPageLinks.dataModel = {
  product: params({
    itemId: stringType().isRequired()
  }).shape({
    dataSource: stringType(),
    identifiers: shapeType({
      canonicalUrl: stringType()
    })
  }),
  storeSearch: params({
    storeSearchInput: stringType().isRequired(),
    pagesize: stringType().isRequired()
  }).shape({
    stores: arrayOf(shapeType({
      storeDetailsPageLink: stringType()
    }))
  })
};

export { RatingReviewPageLinks };